<template>

  <label v-if="label" :for="inputId" :class="['inp-label', {'a11y-blind': hideLabel}, labelClass]">
    {{ label }}
    <span v-if="astRequired" class="text-red">*</span>
  </label>

  <!-- 기본 input + 삭제 버튼 -->
  <div v-if="type === 'text'" class="inp-button-wrap" :class="inpWrapClass">
    <div class="inp-text-wrap calc106">
      <!-- 기본 사이즈 (40px) inp-text클래스에 아래 클래스 추가
          md(중간사이즈 48px), lg(큰사이즈 56px)
      -->
      <input
        :type="type"
        :name="name"
        :id="inputId"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :class="['inp-text', inputClass, customClass]"
        :maxlength="maxlength"
        :readonly="readonly"
        v-model="inputText"
        ref="inputTextRef"
        @input="onInputText"
        @blur="onInputTextBlur"
		    @keyup.enter="handleButtonClick"
      />
      <button type="button" class="btn-delete" aria-label="삭제" v-if="inputText" @click="onInputTextDelete"></button>
    </div>
    <button type="button" :class="['btn', buttonClass]" v-if="showButton" @click="handleButtonClick" :disabled="disabled">{{ buttonText }}</button>
  </div>

  <!-- 비밀번호 + 눈 + 삭제 버튼 -->
  <div v-else-if="type === 'password'" class="inp-button-wrap">
    <div class="inp-text-wrap">
      <input
        autocomplete="new-password"
        :key="`password-${showPassword}`"
        :type="showPassword ? 'text' : 'password'"
        :id="inputId"
        :name="inputId"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        ref="inputPasswordRef"
        v-model="inputPassword"
        :maxlength="max"
        :class="['inp-text password', inputClass]"
        @keyup.enter.prevent.stop="handleEnter"
      />
      <button type="button" class="btn-eye" v-if="showEye1" @click="togglePassword2">
        <span class="on" aria-label="비밀번호 숨기기" v-if="showPassword"></span>
        <span class="off" aria-label="비밀번호 보기" v-else></span>
      </button>
      <button type="button" class="btn-delete" aria-label="삭제" v-if="inputPassword" @click="onPasswordDelete" :disabled="disabled"></button>
    </div>
  </div>

  <!-- number + 삭제 버튼 -->
  <div v-else-if="type === 'number'" class="inp-button-wrap">
    <div class="inp-text-wrap calc106">
      <input
        type="number"
        :name="inputId"
        :id="inputId"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        v-model="inputNumber"
        ref="inputNumberRef"
        @input="onInputNumber"
        inputmode = 'numeric'
        pattern="[0-9]*"
        :class="['inp-text', inputClass]"
        :maxlength="max"
        :readonly="(!isMounted || readonly)"
      />
      <button type="button" class="btn-delete" aria-label="삭제" v-if="inputNumber" @click="onNumberDelete"></button>
    </div>
    <button type="button" :class="['btn', buttonClass]" v-if="showButton" @click="handleButtonClick" :disabled="disabled">{{ buttonText }}</button>
  </div>

  <!-- email -->
  <div v-else-if="type === 'email'" class="form-group email" :class="{'error': isEmailAdrError}">
    <div class="inp-button-wrap">
      <div class="inp-text-wrap email calc106">
        <div class="inp-text-wrap" :class="{'error': isEmailNameError}">
          <input
            type="text"
            :name="inputId"
            :id="inputId"
            placeholder="이메일 앞자리 입력"
            :disabled="disabled"
            :readonly="readonly"
            :required="required"
            :class="['inp-text name', inputClass, customClass]"
            ref="inputEmlNameRef"
            v-model="inputEmailName"
            :maxlength="max"
            />
            <button type="button" class="btn-delete" aria-label="삭제" v-if="inputEmailName" @click="onInputEmailNameDelete"></button>
        </div>
        <span>@</span>
        <div class="inp-text-wrap" :class="{'error': isEmailDomainError}">
          <input
            type="text"
            :name="inputIdSecond"
            :id="inputIdSecond"
            placeholder="도메인 직접입력/선택"
            :disabled="disabled"
            :readonly="readonly"
            :required="required"
            :class="['inp-text domain', inputClass]"
            ref="inputEmlDomainRef"
            v-model="inputEmailDomain"
            :maxlength="max"
            />
          <button type="button" class="btn-delete" aria-label="삭제" v-if="inputEmailDomain" @click="onInputEmailDomainDelete"></button>
        </div>
        <HconDropDown
          type="dropdownBox" 
          v-model="emailDomainOption"
          :disabled="(disabled || isReadonly)"
          :selectBoxTitleClass="selectBoxTitleClass" 
          :dropdownOptions="emailDomainOptions" />
      </div>
      <button type="button" :class="['btn', buttonClass]" v-if="showButton" @click="handleButtonClick" :disabled="disabled">{{ buttonText }}</button>
    </div>
    <p v-if="isEmailNameError && isEmailNameErrorType == 'null'" class="helper-text error">{{HconCommonUtils.format.message(COMMON_CODE.ALT_HCON_9001,"이메일 앞자리를","입력")}}</p>
    <p v-if="isEmailDomainError && isEmailDomainErrorType == 'null'" class="helper-text error">{{HconCommonUtils.format.message(COMMON_CODE.ALT_HCON_9001,"이메일 뒷자리 도메인을","입력")}}</p>
    <p v-if="isEmailAdrError && isEmailAdrErrorType == 'format'" class="helper-text error">{{HconCommonUtils.format.message(COMMON_CODE.ALT_HCON_9008,"이메일")}}</p>
  </div>

  <div v-else-if="type === 'tel'" class="form-group tel">
    <div class="inp-button-wrap">
      <div class="inp-text-wrap tel calc106">
        <!-- 일반 전화번호 드롭다운 -->
        <HconDropDown
          v-if="!isLoadingCbleTelno" 
          type="commDropdownBox"
          v-model="areaNoOption"
          placeholder="국번 선택" 
          display-options="etc"
          :commDropdownOptions="hcAreaNoCd"
          :selectBoxTitleClass="selectBoxTitleClass"
          :customClass="{'error': isAreaNoOptionError}"
          :disabled="(disabled || isReadonly)" />
        <div v-if="areaNoOption == '999'" class="inp-text-wrap" :class="{'error': isInputAreaNoError}">
          <!-- 국번 직접입력  -->
          <input
            type="text"
            :name="inputId"
            :id="inputId"
            placeholder="000"
            maxlength="3"
            :disabled="disabled"
            :required="required"
            :class="['inp-text tel02', inputClass]"
            ref="inputAreaNoRef"
            v-model="inputAreaNo"
            pattern="[0-9]*"
            inputmode = 'numeric'
            :readonly="(!isMounted || readonly)"
            />
          <!-- 국번 직접입력 삭제 버튼 -->
          <button type="button" class="btn-delete" aria-label="삭제" v-if="inputAreaNo" @click="onInputAreaNoDelete"></button>
        </div>
        <span>-</span>
        <div class="inp-text-wrap" :class="{'error': isTelSecondError}">
          <!-- 일반 전화번호 중간자리 -->
          <input
            type="text"
            :name="inputIdSecond"
            :id="inputIdSecond"
            placeholder="0000"
            maxlength="4"
            :disabled="disabled"
            :required="required"
            :class="['inp-text tel02', inputClass]"
            ref="inputTelSecondRef"
            v-model="inputTelSecond"
            pattern="[0-9]*"
            inputmode = 'numeric'
            :readonly="(!isMounted || readonly)"
            />
          <!-- 일반 전화번호 중간자리 삭제 버튼 -->
          <button type="button" class="btn-delete" aria-label="삭제" v-if="inputTelSecond" @click="onInputTelSecondDelete"></button>
        </div>
        <span>-</span>
        <div class="inp-text-wrap" :class="{'error': isTelThirdError}">
          <!-- 일반 전화번호 끝자리 -->
          <input
            type="text"
            :name="inputIdThird"
            :id="inputIdThird"
            placeholder="0000"
            maxlength="4"
            :disabled="disabled"
            :required="required"
            :class="['inp-text tel03', inputClass]"
            ref="inputTelThirdRef"
            v-model="inputTelThird"
            inputmode = 'numeric'
            :readonly="(!isMounted || readonly)"
            />
          <!-- 일반 전화번호 끝자리 삭제 버튼 -->
          <button type="button" class="btn-delete" aria-label="삭제" v-if="inputTelThird" @click="onInputTelThirdDelete"></button>
        </div>
      </div>
      <button type="button" :class="['btn', buttonClass]" v-if="showButton" @click="handleButtonClick">{{ buttonText }}</button>
    </div>
     <!-- 일반 전화번호 validate체크 영역 -->
    <p v-if="isAreaNoOptionError && isAreaNoOptionErrorType == 'null'" class="helper-text error">{{HconCommonUtils.format.message(COMMON_CODE.ALT_HCON_9001,"지역번호를","선택")}}</p>  
    <p v-if="isInputAreaNoError && isInputAreaNoErrorType == 'null'" class="helper-text error">{{HconCommonUtils.format.message(COMMON_CODE.ALT_HCON_9001,"지역번호 직접입력을","입력")}}</p>  
    <p v-if="isTelSecondError && isTelSecondErrorType == 'null'" class="helper-text error">{{HconCommonUtils.format.message(COMMON_CODE.ALT_HCON_9001,"전화번호 앞자리를","입력")}}</p>
    <p v-if="isTelThirdError && isTelThirdErrorType == 'null'" class="helper-text error">{{HconCommonUtils.format.message(COMMON_CODE.ALT_HCON_9001,"전화번호 뒷자리를","입력")}}</p>
    <p v-if="isCbleTelnoError && isCbleTelnoErrorType == 'type'" class="helper-text error">{{HconCommonUtils.format.message(COMMON_CODE.ALT_HCON_9003,"전화번호는","숫자")}}</p>
    <p v-html="HconCommonUtils.format.message(MEMBER_CODE.ALT_HCON_9037)" v-if="isCbleTelnoError && isCbleTelnoErrorType == 'format'" class="helper-text error"></p>
  </div>

  <div v-else-if="type === 'rpsTel'" class="form-group tel">
    <div class="inp-button-wrap">
      <div class="inp-text-wrap tel calc106">
        <!-- 부가서비스 전화번호 드롭다운 -->
        <HconDropDown
          v-if="!isLoadingRpsTelno" 
          type="commDropdownBox"
          v-model="rpsNoOption"
          placeholder="부가서비스 선택" 
          display-options="etc"
          :commDropdownOptions="hcRpsNoCd"
          :selectBoxTitleClass="selectBoxTitleClass"
          :customClass="{'error': isRpsNoOptionError}" :disabled="(disabled || isReadonly)" />
        <div v-if="rpsNoOption == '9999'" class="inp-text-wrap" :class="{'error': isInputRpsNoError}">
          <!-- 부가서비스 전화번호 직접입력 -->
          <input
            type="text"
            :name="inputId"
            :id="inputId"
            placeholder="0000"
            maxlength="4"
            :disabled="disabled"
            :required="required"
            :readonly="readonly"
            :class="['inp-text tel02', inputClass]"
            ref="inputRpsNoRef"
            v-model="inputRpsNo"
            pattern="[0-9]*"
            inputmode = 'numeric'
            />
          <!-- 부가서비스 전화번호 직접입력 삭제 버튼 -->
          <button type="button" class="btn-delete" aria-label="삭제" v-if="inputRpsNo" @click="onInputRpsNoDelete"></button>
        </div>
        <span>-</span>
        <div class="inp-text-wrap" :class="{'error': isRpsTelSecondError}">
          <!-- 부가서비스 전화번호 뒷자리 -->
          <input
            type="text"
            :name="inputIdSecond"
            :id="inputIdSecond"
            placeholder="0000"
            maxlength="4"
            :disabled="disabled"
            :readonly="readonly"
            :required="required"
            :class="['inp-text tel02', inputClass]"
            ref="inputRpsTelSecondRef"
            v-model="inputRpsTelSecond"
            pattern="[0-9]*"
            inputmode = 'numeric'
            />
          <!-- 부가서비스 전화번호 뒷자리 삭제 버튼-->
          <button type="button" class="btn-delete" aria-label="삭제" v-if="inputRpsTelSecond" @click="onInputRpsTelSecondDelete"></button>
        </div>
      </div>
      <button type="button" :class="['btn', buttonClass]" v-if="showButton" @click="handleButtonClick" :disabled="disabled">{{ buttonText }}</button>
    </div>
    <!-- 부가서비스 전화번호 validate체크 영역 -->
    <p v-if="isRpsNoOptionError && isRpsNoOptionErrorType == 'null'" class="helper-text error">{{HconCommonUtils.format.message(COMMON_CODE.ALT_HCON_9001,"부가서비스 전화번호를","선택")}}</p>  
    <p v-if="isInputRpsNoError && isInputRpsNoErrorType == 'null'" class="helper-text error">{{HconCommonUtils.format.message(COMMON_CODE.ALT_HCON_9001,"부가서비스 직접입력을","입력")}}</p>  
    <p v-if="isRpsTelSecondError && isRpsTelSecondErrorType == 'null'" class="helper-text error">{{HconCommonUtils.format.message(COMMON_CODE.ALT_HCON_9001,"전화번호 뒷자리를","입력")}}</p>
    <p v-if="isRpsTelnoError && isRpsTelnoErrorType == 'type'" class="helper-text error">{{HconCommonUtils.format.message(COMMON_CODE.ALT_HCON_9003,"전화번호는","숫자")}}</p>
    <p v-html="HconCommonUtils.format.message(MEMBER_CODE.ALT_HCON_9170)" v-if="isRpsTelnoError && isRpsTelnoErrorType == 'format'" class="helper-text error"></p>
  </div>

  <!-- 사업자등록번호 -->
  <div v-else-if="type === 'business'" class="inp-button-wrap">
    <div class="inp-text-wrap calc106">
      <input
        type="text"
        :class="['inp-text', inputClass]"
        :id="inputId"
        :name="inputId"
        :disabled="disabled"
        :required="required"
        v-model="inputNumber"
        ref="inputNumberRef"
        @input="onInputNumber"
        inputmode = 'numeric'
        pattern="[0-9]*"
        maxlength="10"
        placeholder="하이픈(-) 없이 10자리 숫자를 입력하세요"
        :readonly="(!isMounted || readonly)"
      />
      <button 
        type="button" 
        class="btn-delete" 
        aria-label="삭제" 
        v-if="inputNumber" 
        @click="onNumberDelete"></button>
    </div>
    <button type="button" :class="['btn', buttonClass]" v-if="showButton" @click="handleButtonClick" :disabled="disabled">{{ buttonText }}</button>
  </div>

  <!-- 파일 업로드 -->
  <div v-else-if="type === 'upload'" class="inp-button-wrap">
    <div class="inp-text-wrap file-upload calc106" :class="{'inp-file-hidden': showFileInput}">
      <input
        v-model="uploadName"
        type="text"
        :class="['inp-text upload-name', inputClass]"
        :id="inputId"
        :name="inputId"
        :required="required"
        :disabled="disabled"
        placeholder="파일은 png, jpg, pdf 형식의 2MB 이하로 등록하시기 바랍니다"
        readonly
      />
      <input type="file" id="ex_filename" class="upload-hidden" @change="handleFileChange" />
    </div>
    <label :class="['btn', 'upload-label', buttonClass]" v-if="showButton">
      {{ buttonText }}
      <input type="file" class="upload-hidden" @change="handleFileChange" :disabled="disabled" />
    </label>
    <button type="button" :class="['btn-plus', plusButtonClass]" v-if="plusShowButton" @click="handlePlusBtn" :disabled="disabled">{{ plusButtonText }}</button>
  </div>

  <!-- 달력 -->
  <div v-else-if="type === 'date'" class="inp-button-wrap">
    <div class="inp-text-wrap date">
      <HconComPCDatepicker :disabled="disabled" />
      <button type="button" class="btn-calendar" :disabled="disabled"></button>
    </div>
  </div>

  <!-- 메시지 -->
  <div v-else-if="type === 'message'" class="inp-button-wrap">
    <div class="inp-text-wrap message calc106">
      <input
        type="text"
        :name="inputId"
        :id="inputId"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :readonly="readonly"
        :class="['inp-text message', inputClass]"
        :value="modelValue"
        ref="inputMessageRef"
        :maxlength="max"
        v-bind="$attrs"
        @input="onInputMessage"
        @blur="onInputMsgBlur"
      />
      <p :class="['counter', msgCounterClass]"><span class="text-green">{{ String(modelValue ? modelValue : '').length }}</span>/{{ max }}</p>
    </div>
  </div>

  <!-- 계좌정보 -->
  <div v-else-if="type === 'account'" class="inp-button-wrap">
    <div class="inp-text-wrap account calc106">
      <HconDropDown v-if="!isLoadingAccount"
        type="commDropdownBox"
        v-model="accountModel.bankCode"
        placeholder="은행선택"
        :commDropdownOptions="hcFnnsCd"
        @update:model-value="onBankCodeChange"
        :disabled="(disabled || isReadonly)" />
      <div class="inp-text-wrap">
        <input
          type="text"
          :name="inputId"
          :id="inputId"
          placeholder="계좌번호를 '-'없이 입력해주세요"
          :disabled="disabled"
          :required="required"
          :class="['inp-text bank-num', inputClass]"
          inputmode = 'numeric'
          pattern="[0-9]*"
          @input="onAccountNumInput"
          v-model="accountModel.accountNumber"
          :readonly="(!isMounted || readonly)"
          />
        <button type="button" class="btn-delete" aria-label="삭제" v-if="accountModel.accountNumber" @click="onAccountNumInpDelete"></button>
      </div>
      <div class="inp-text-wrap">
        <input
          type="text"
          :name="inputIdSecond"
          :id="inputIdSecond"
          placeholder="예금주명을 입력해주세요"
          :disabled="disabled"
          :readonly="readonly"
          :required="required"
          :class="['inp-text bank-name', inputClass]"
          v-model="accountModel.depositor"
          />
        <button type="button" class="btn-delete" aria-label="삭제" v-if="accountModel.depositor" @click="onAccountNameInpDelete"></button>
      </div>
      <div class="inp-text-wrap">
        <input
          type="text"
          :name="inputId"
          :id="inputId"
          placeholder="식별번호를 입력해주세요"
          :disabled="disabled"
          :readonly="readonly"
          :required="required"
          :class="['inp-text bank-identifyNo', inputClass]"
          inputmode = 'numeric'
          pattern="[0-9]*"
          @input="onAccountIdentifyNoInput"
          v-model="accountModel.identifyNo"
          />
        <button type="button" class="btn-delete" aria-label="삭제" v-if="accountModel.identifyNo" @click="onAccountIdentifyNoInpDelete"></button>
      </div>
    </div>
    <button type="button" :class="['btn', buttonClass]" v-if="showButton" @click="handleAccountBtn" :disabled="disabled">{{ buttonText }}</button>
  </div>
  <p v-if="isNoBankCdError && isNoBankCdErrorType == 'null'" class="helper-text error mt0">{{HconCommonUtils.format.message(COMMON_CODE.ALT_HCON_9001,"은행을","선택")}}</p>
  <p v-if="isNoAccountError && isNoAccountErrorType == 'null'" class="helper-text error mt0">{{HconCommonUtils.format.message(COMMON_CODE.ALT_HCON_9001,"계좌번호/예금주명/생년월일을","입력")}}</p>
  <p v-if="isAccountNumError && isAccountNumErrorType == 'null'" class="helper-text error mt0">{{HconCommonUtils.format.message(COMMON_CODE.ALT_HCON_9003,"계좌번호는","숫자")}}</p>
  <!-- 원 -->
  <div v-else-if="type === 'pay'" class="inp-button-wrap" :class="inpWrapClass">
    <div class="inp-text-wrap pay calc106">
      <input
        type="text"
        :name="inputId"
        :id="inputId"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :readonly="readonly"
        :value="inputPayNumDisplay"
        :minlength="min"
        :maxlength="max"
        @input="onInputPayNumber"
        @blur="onInputPayBlur"
        :class="['inp-text', inputClass]"
      />
      <span class="unit">{{ unit }}</span>
    </div>
    <button type="button" :class="['btn', buttonClass]" v-if="showButton" @click="handleButtonClick" :disabled="disabled">{{ buttonText }}</button>
  </div>
  <!-- 검색 -->
  <div v-else-if="type === 'search'" class="inp-button-wrap" :class="inpWrapClass">
    <div class="inp-text-wrap search calc106">
      <input
        type="text"
        :name="inputId"
        :id="inputId"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        v-model="inputText"
        :maxlength="max"
        @input="onInputText"
        :class="['inp-text', inputClass]"
      />
      <button type="button" class="btn-delete" aria-label="삭제" v-if="inputText" @click="onInputTextDelete"></button>
      <button type="button" class="btn-search" aria-label="검색" @click="handleSearch"></button>
    </div>
    <button type="button" :class="['btn', buttonClass]" v-if="showButton" :disabled="disabled">{{ buttonText }}</button>
  </div>

  <!-- counter -->
  <div v-else-if="type === 'count'" class="inp-button-wrap" :class="inpWrapClass">
    <div class="inp-text-wrap inp-count" :class="inpCntClass">
      <button type="button" class="minus" :disabled="(currentNumber <= (actualMin ?? 0) || inpCntClass === 'disabled')" @click="onMinus"></button>
      <input
        type="text"
        :name="inputId"
        :id="inputId"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :value="currentNumber"
        inputmode="numeric"
        @input="onInputCount"
        @blur="onBlurCount"
        @paste="onPasteCount"
        :class="['inp-text', inputClass]"
      />
      <button type="button" class="plus" :disabled="(currentNumber >= (props.max ?? 0) || inpCntClass === 'disabled')" @click="onPlus"></button>
    </div>
  </div>

</template>

<script setup lang="ts">
import HconDropDown from '@/components/com/HconComPCDropDown.vue';
import HconComPCDatepicker from '@/components/com/HconComPCDatepicker.vue';
import { useId, computed, nextTick, ref, type Ref } from 'vue';
import { HconCommonUtils } from "@/utils/hcon-common-utils"
import { COMMON_CODE, MEMBER_CODE } from "@/constants/hconMessageConstants";
import { useHconRefCommonCode } from "~/composables/hcon-ref-common-code";
import { HconCommonRegExp } from '@/constants/hconRegexConstants'

type InputType = 'text' | 'password' | 'number' | 'email' | 'business' 
                | 'upload' | 'date' | 'tel'  | 'rpsTel' | 'message' | 'account' | 'pay' | 'search'
                | 'count' | 'telObj';

const inputId = useId();
const inputIdSecond = useId();
const inputIdThird = useId();

// 부모컴포넌트에서 해당 변수 사용
const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, Object],
    default: ''
  },
  label: {
    type: String,
    default: ''
  },
  hideLabel: {
    type: Boolean,
    default: false
  },
  labelClass: {
    type: String,
    default: ''
  },
  type: {
    type: String as () => InputType,
    default: 'text' as InputType,
    validator: (value: any):value is InputType => ['text', 'password', 'number', 'email',
                                                  'business', 'upload', 'date', 'tel', 'rpsTel', 'message',
                                                  'account', 'pay', 'search', 'count', 'telObj'].includes(value)
  },
  buttonClass: {
    type: String,
    default: ''
  },
  inputClass: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  error: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  astRequired: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  showButton: {
    type: Boolean,
    default: false
  },
  buttonText: {
    type: String,
    default: '',
  },
  selectBoxTitleClass: {
    type: String,
    default: ''
  },
  maxlength: {
    type: Number,
    default: undefined
  },
  max: {
    type: Number,
    default: undefined
  },
  min: {
    type: Number,
    default: undefined,
  },
  isCashable: {
    type: Boolean,
    default: false
  },
  cashableMin: {
    type: Number,
    default: 20
  },
  plusButtonClass: {
    type: String,
    default: ''
  },
  plusShowButton: {
    type: Boolean,
    default: false
  },
  plusButtonText: {
    type: String,
    default: ''
  },
  msgCounterClass: {
    type: String,
    default: ''
  },
  inputOptions: {
    type: Object,
    default: () => [
    { commDtlCd: 'value', commDtlCdNm: 'label' },
    ]
  },
  bankValue: {
    type: String,
    default: ''
  },
  bankOptions: {
    type: Array,
    default: () => ['신한은행', '국민은행', '우리은행', '하나은행', '농협은행']
  },
  accountValue: {
    type: Object,
    // default: ''
  },
  accountNameValue: {
    type: String,
    default: ''
  },
  accountNumValue: {
    type: String,
    default: ''
  },
  inpWrapClass: {
    type: String,
    default: ''
  },
  name: {
    type: String,
    default: ''
  },
  unit: {
    type: String,
    default: '원'
  },
  telValue: { // 유선전화번호 전용 prop
    type: Object,
  },
  rsptelValue: { // 대표전화번호(부가서비스번호) 전용 prop
    type: Object,
  },
  showFileInput: {
    type: Boolean,
    default: true
  },
  customClass: {
    type: [Boolean, String, Object],
    default: () => ({})
  },
  pattern: {
    type: String
  },
  inpCntClass: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'buttonClick', 'plusBtnClick',
                          'iputDelete', 'update:bankValue', 'update:accountValue', 'accountBtnClick', 'blur',
                          'searchBtnClick', 'update:telValue', 'update:rsptelValue', 'enter']);


const handleButtonClick = () => {
  emit('buttonClick')  
} 

// 로딩 플래그
const loadingFlag = ref(false)

const handleEnter = (e: KeyboardEvent) => {

  if(e.key === 'Enter') {

    if(loadingFlag.value === true) return

    loadingFlag.value = true;
    
    emit('enter')
  }
   
}

/**
 * type = text
 */

// Input Text 변수 선언
const inputText = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value);
  }
});

const inputTextRef: Ref<HTMLInputElement | null> = ref(null);

// 정규식
const patternMap = {
  'num' : /[^0-9]/g,
  'kor_eng' : /[^a-zA-Zㄱ-하-ㅣ가-힣]/g
}

// Input Text Input 함수
const onInputText = (event: Event): void => {
  const target = event.target as HTMLInputElement;
  let value = target.value;

  // max 값 적용
  if(props.maxlength) {
    value = value.slice(0, props.maxlength);
  }
  // pattern 값 적용
  if(props.pattern) {
    const regex = patternMap[props.pattern as keyof typeof patternMap]
    value = value.replace(regex, '')
  }
  
  target.value = value

  emit('update:modelValue', value);

}

// Input Text Delete 함수
const onInputTextDelete = () => {
  inputText.value = "";
  onInputTextFocus();
  emit('iputDelete');
}
// Input Text Focus 함수
const onInputTextFocus = (): void => {
  if(inputTextRef.value) {
    inputTextRef.value?.focus();
  }
}

const onInputTextBlur = (event: Event): void => {
  const target = event.target as HTMLInputElement
  emit('blur', target.value)
}

/**
 * type = email
 */

// 이메일 서비스 도메인 리스트
const emailDomainOptions = [
  { key: 'etc', value: '직접입력' },
  { key: 'naver.com', value: 'naver.com' },
  { key: 'gmail.com', value: 'gmail.com' },
  { key: 'daum.net', value: 'daum.net' },
  { key: 'handmail.net', value: 'handmail.net' },
  { key: 'nate.com', value: 'nate.com' },
  { key: 'kakao.com', value: 'kakao.com' },
] as const;

// 부모 화면에서 받아온 email 전체 값 (font@back 형식)
const emailAdr = defineModel({
  type: String,
  default: '',
});

const isEmailAdrError = ref(false)
const isEmailAdrErrorType = ref()

// 이메일 option에서 선택한 값
type domainOption = typeof emailDomainOptions[number];
type domainKey = domainOption['key'];
type selectedDomain = domainKey | '';
const emailDomainOption = ref<selectedDomain>('');

// 이메일 이름 변수 선언
const inputEmailName = computed({
  get: () => emailAdr.value.split('@')[0] || '',
  set: (value) => {
    const [, back = ''] = emailAdr.value.split('@');
    emailAdr.value = `${value}${back ? '@' + back : ''}`
  }
});

const inputEmlNameRef: Ref<HTMLInputElement | null> = ref(null);
const isEmailNameError = ref(false)
const isEmailNameErrorType = ref()

// 이메일 도메인 변수 선언
const inputEmailDomain = computed({
  get: () => emailAdr.value.split('@')[1] || '',
  set: (value) => {
    const [front = ''] = emailAdr.value.split('@');
    emailAdr.value = `${front}${value ? '@' + value : ''}`
  }
})

const inputEmlDomainRef: Ref<HTMLInputElement | null> = ref(null);
const isEmailDomainError = ref(false)
const isEmailDomainErrorType = ref()

// 이메일 필수 값 체크 함수
const onEmailAdrValidate = (isCheck: boolean) => {
  
  const checkEmailFlag = ref(false);

  if(isCheck == false) {
    isEmailNameError.value = false
    isEmailNameErrorType.value = ""
    isEmailDomainError.value = false
    isEmailDomainErrorType.value = ""
    isEmailAdrError.value = false
    isEmailAdrErrorType.value = ""
    return checkEmailFlag.value = false
  }
  else if (isCheck == true) {

    isEmailNameError.value = false
    isEmailNameErrorType.value = ""
    isEmailDomainError.value = false
    isEmailDomainErrorType.value = ""
    isEmailAdrError.value = false
    isEmailAdrErrorType.value = ""

    // 이메일 이름 Null 체크
    if(inputEmailName.value == null || inputEmailName.value == "") {
      onInputEmailNameFocus();
      isEmailNameError.value = true
      isEmailNameErrorType.value = 'null'
      return checkEmailFlag.value = false; 
    }

    isEmailNameError.value = false
    isEmailNameErrorType.value = ""

    // 이메일 도메인 Null 체크
    if(inputEmailDomain.value == null || inputEmailDomain.value == "") {
      onInputEmailDomainFocus();
      isEmailDomainError.value = true
      isEmailDomainErrorType.value = 'null'
      return checkEmailFlag.value = false;
    }

    isEmailDomainError.value = false
    isEmailDomainErrorType.value = ""
    
    // 이메일 형식 체크
    if(!HconCommonRegExp.EMAIL_REGEX.test(emailAdr.value)) {
      onInputEmailNameFocus();
      isEmailAdrError.value = true
      isEmailAdrErrorType.value = 'format'
      return checkEmailFlag.value = false;
    }

    isEmailAdrError.value = false
    isEmailAdrErrorType.value = ""

    return checkEmailFlag.value = true
  }

  return checkEmailFlag.value
}

// 이메일 이름 Delete 함수
const onInputEmailNameDelete = () => {
  inputEmailName.value = "";
}

// 이메일 이름 Focus 함수
const onInputEmailNameFocus = (): void => {
  if(inputEmlNameRef.value) {
    inputEmlNameRef.value?.focus();
  }
}

//이메일 도메인 Delete 함수
const onInputEmailDomainDelete = () => {
  inputEmailDomain.value = "";
  emailDomainOption.value = "";
}

// 이메일 도메인 Focus 함수
const onInputEmailDomainFocus = (): void => {
  if(inputEmlDomainRef.value) {
    inputEmlDomainRef.value?.focus();
  }
}

watch(emailDomainOption, (newVal) => {
  if (!newVal) return;

  if (emailDomainOption.value === 'etc') {
    // 도메인 직접입력 영역 활성화
    inputEmailDomain.value = "";
  } else {
    // 도메인 직접입력 영역에 선택한 도메인 자동 세팅
    inputEmailDomain.value = emailDomainOption.value;
  }
});

/**
 * type = tel
 */
 const {codeDataList:hcAreaNoCd, isLoading: isLoadingCbleTelno} = useHconRefCommonCode('hc_area_no_cd', {enabled: props.type === 'tel'}); //HCON지역번호코드

// 일반 전화번호 옵션
const areaNoOption = ref("");
const isAreaNoOptionError = ref(false)
const isAreaNoOptionErrorType = ref("")
// 일반 전화번호 Full 값
const cbleTelno = ref("");
const isCbleTelnoError = ref(false)
const isCbleTelnoErrorType = ref("")


// 국번 직접입력 변수 선언
const inputAreaNo = computed({
  get: () => cbleTelno.value.split('-')[1] || '',
  set: (value) => {
    const getValue = cbleTelno.value.split('-')

    getValue[1] = value

    cbleTelno.value = getValue.join('-')

    emit('update:modelValue', cbleTelno.value);
  }
});

const inputAreaNoRef: Ref<HTMLInputElement | null> = ref(null);
const isInputAreaNoError = ref(false)
const isInputAreaNoErrorType = ref("")

// 전화번호 중간자리 변수 선언
const inputTelSecond = computed({
  get: () => areaNoOption.value == '999' ? cbleTelno.value.split('-')[2] || '' : cbleTelno.value.split('-')[1] || '',
  set: (value) => {
    const getValue = cbleTelno.value.split('-')

    areaNoOption.value == '999' ? getValue[2] = value : getValue[1] = value
    
    cbleTelno.value = getValue.join('-')
     
    emit('update:modelValue', cbleTelno.value);
  }
});

const inputTelSecondRef: Ref<HTMLInputElement | null> = ref(null);
const isTelSecondError = ref(false)
const isTelSecondErrorType = ref()

// 전화번호 끝자리 변수 선언
const inputTelThird = computed({
  get: () => areaNoOption.value == '999' ? cbleTelno.value.split('-')[3] || '' : cbleTelno.value.split('-')[2] || '',
  set: (value) => {
    const getValue = cbleTelno.value.split('-')

    areaNoOption.value == '999' ? getValue[3] = value : getValue[2] = value

    cbleTelno.value = getValue.join('-')

    emit('update:modelValue', cbleTelno.value);
  }
});

const inputTelThirdRef: Ref<HTMLInputElement | null> = ref(null);
const isTelThirdError = ref(false)
const isTelThirdErrorType = ref()

// 일반 전화번호 값 헨들링
watch(areaNoOption, (newVal, oldVal) => {
  if (!newVal) return;

  if(oldVal) {
    inputAreaNo.value =''
    inputTelSecond.value = ''
    inputTelThird.value = ''
  }

  if(areaNoOption.value == '999') {
    cbleTelno.value = `${areaNoOption.value}-${inputAreaNo.value || ''}-${inputTelSecond.value || ''}-${inputTelThird.value || ''}`
  }
  else {
    cbleTelno.value = `${areaNoOption.value || ''}-${inputTelSecond.value || ''}-${inputTelThird.value || ''}`
  }
  emit('update:modelValue', cbleTelno.value);
   
});


// 조회한 일반 전화번호 세팅
watch(
  () => props.modelValue,
  (value) => {
    if (!value) {
      return;
    }
    if (props.type === 'tel' && String(value).slice(0,3) != '999') {
      const [a, m, l] = String(value).split('-');
      areaNoOption.value = a || '';
      inputTelSecond.value = m || '';
      inputTelThird.value = l || '';
    } else if(props.type === 'tel' && String(value).slice(0,3) == '999'){
      const [a, m, l, z] = String(value).split('-');
      areaNoOption.value = a || '';
      inputAreaNo.value = m || '';
      inputTelSecond.value = l || '';
      inputTelThird.value = z || '';
    }
  }
)

// 국번 직접입력 Delete 함수
const onInputAreaNoDelete = () => {
  const getValue = cbleTelno.value.split('-')

  getValue[1] = ""

  cbleTelno.value = getValue.join('-')
}

// 국번 직접입력 Focus 함수
const onInputAreaNoFocus = () => {
  if(inputAreaNoRef.value) {
    inputAreaNoRef.value?.focus();
  }
}

// 전화번호 중간자리 Delete 함수
const onInputTelSecondDelete = () => {
  const getValue = cbleTelno.value.split('-')

  areaNoOption.value == '999' ? getValue[2] = "" : getValue[1] = ""

  cbleTelno.value = getValue.join('-')
}

// 전화번호 중간자리 Focus 함수
const onInputTelSecondFocus = (): void => {
  if(inputTelSecondRef.value) {
    inputTelSecondRef.value?.focus();
  }
}

// 전화번호 끝자리 Delete 함수
const onInputTelThirdDelete = () => {
  const getValue = cbleTelno.value.split('-')

  areaNoOption.value == '999' ? getValue[3] = "" : getValue[2] = ""

  cbleTelno.value = getValue.join('-')
}

// 전화번호 끝자리 Focus 함수
const onInputTelThirdFocus = (): void => {
  if(inputTelThirdRef.value) {
    inputTelThirdRef.value?.focus();
  }
}

// 유선 전화번호 필수 값 체크 함수
const onCbleTelnoValidate = (isCheck: boolean) => {
  
  const checkCbleTelnoFlag = ref(true);

  if(isCheck == false) {
    isAreaNoOptionError.value = false
    isAreaNoOptionErrorType.value = ""
    isInputAreaNoError.value = false
    isInputAreaNoErrorType.value = ""
    isTelSecondError.value = false
    isTelSecondErrorType.value = ""
    isTelThirdError.value = false
    isTelThirdErrorType.value = ""
    isCbleTelnoError.value = false
    isCbleTelnoErrorType.value = ""
    return checkCbleTelnoFlag.value = false
  }
  else if (isCheck == true) {
    
    isAreaNoOptionError.value = false
    isAreaNoOptionErrorType.value = ""
    isInputAreaNoError.value = false
    isInputAreaNoErrorType.value = ""
    isTelSecondError.value = false
    isTelSecondErrorType.value = ""
    isTelThirdError.value = false
    isTelThirdErrorType.value = ""
    isCbleTelnoError.value = false
    isCbleTelnoErrorType.value = ""
    
    // 지역번호 Null 체크
    if(areaNoOption.value == null || areaNoOption.value == "") {
      isAreaNoOptionError.value = true
      isAreaNoOptionErrorType.value = 'null'
      return checkCbleTelnoFlag.value = false;
    }

    isAreaNoOptionError.value = false
    isAreaNoOptionErrorType.value = ""
    
    // 지역번호 직접입력 Null 체크
    if(areaNoOption.value =='999' && (inputAreaNo.value == null || inputAreaNo.value == "")) {        
      onInputAreaNoFocus();
      isInputAreaNoError.value = true
      isInputAreaNoErrorType.value = 'null'
      return checkCbleTelnoFlag.value = false;
    }

    isInputAreaNoError.value = false
    isInputAreaNoErrorType.value = ""

    // 전화번호 중간자리 Null 체크
    if(inputTelSecond.value == null || inputTelSecond.value == "") {
      onInputTelSecondFocus();
      isTelSecondError.value = true
      isTelSecondErrorType.value = 'null'
      return checkCbleTelnoFlag.value = false;
    }
    
    isTelSecondError.value = false
    isTelSecondErrorType.value = ""

    // 전화번호 끝자리 Null 체크
    if(inputTelThird.value == null || inputTelThird.value == "") {
      onInputTelThirdFocus();
      isTelThirdError.value = true
      isTelThirdErrorType.value = 'null' 
      return checkCbleTelnoFlag.value = false;
    }

    isTelThirdError.value = false
    isTelThirdErrorType.value = ""

    // 전화번호 타입 체크
    if((areaNoOption.value =='999' && isNaN(Number(inputAreaNo.value))) || isNaN(Number(inputTelSecond.value)) || isNaN(Number(inputTelThird.value))) {
      if(areaNoOption.value =='999' && isNaN(Number(inputAreaNo.value))) {
        onInputAreaNoFocus();
        isInputAreaNoError.value = true
        isInputAreaNoErrorType.value = 'type'
      }
      else if(isNaN(Number(inputTelSecond.value))) {
        onInputTelSecondFocus();
        isTelSecondError.value = true
        isTelSecondErrorType.value = 'type'
      } 
      else {
        onInputTelThirdFocus();
        isTelThirdError.value = true
        isTelThirdErrorType.value = 'type'
      }

      isCbleTelnoError.value = true
      isCbleTelnoErrorType.value = 'type'
      return checkCbleTelnoFlag.value = false;
    }

    isInputAreaNoError.value = false
    isInputAreaNoErrorType.value = ""
    isTelSecondError.value = false
    isTelSecondErrorType.value = ""
    isTelThirdError.value = false
    isTelThirdErrorType.value = ""
    isCbleTelnoError.value = false
    isCbleTelnoErrorType.value = ""
    
    // 전화번호 정규식 체크
    if(!HconCommonRegExp.Cbl_TELNO_REGEX.test(areaNoOption.value !='999' ? cbleTelno.value : cbleTelno.value.substring(cbleTelno.value.indexOf('-') +1))) {
      if(![3,4].includes(inputTelSecond.value.length)) {
        onInputTelSecondFocus();
        isTelSecondError.value = true
        isTelSecondErrorType.value = 'format'
      } 
      else if(inputTelThird.value.length != 4) {
        onInputTelThirdFocus();
        isTelThirdError.value = true
        isTelThirdErrorType.value = 'format' 
      } 
      else {
        onInputAreaNoFocus();
        isInputAreaNoError.value = true
        isInputAreaNoErrorType.value = 'format'
      }
      
      isCbleTelnoError.value = true
      isCbleTelnoErrorType.value = 'format'
      return checkCbleTelnoFlag.value = false;
    }

    isInputAreaNoError.value = false
    isInputAreaNoErrorType.value = ""
    isTelSecondError.value = false
    isTelSecondErrorType.value = ""
    isTelThirdError.value = false
    isTelThirdErrorType.value = ""
    isCbleTelnoError.value = false
    isCbleTelnoErrorType.value = ""

  }

  return checkCbleTelnoFlag.value = true

}

/**
 * type = rpsTel
 */
 const {codeDataList:hcRpsNoCd, isLoading: isLoadingRpsTelno} = useHconRefCommonCode('hc_rps_no_cd', {enabled: props.type === 'rpsTel'}); //HCON대표번호코드


// 부가서비스 전화번호 옵션
const rpsNoOption = ref("");
const isRpsNoOptionError = ref(false)
const isRpsNoOptionErrorType = ref("")
// 부가서비스 전화번호 Full 값
const rpsTelno = ref("");
const isRpsTelnoError = ref(false)
const isRpsTelnoErrorType = ref("")

// 부가서비스 전화번호 직접입력 변수 선언
const inputRpsNo = computed({
  get: () => rpsTelno.value.split('-')[1] || '',
  set: (value) => {
    const getValue = rpsTelno.value.split('-')

    getValue[1] = value

    rpsTelno.value = getValue.join('-')

    emit('update:modelValue', rpsTelno.value);
  }
});

const inputRpsNoRef: Ref<HTMLInputElement | null> = ref(null);
const isInputRpsNoError = ref(false)
const isInputRpsNoErrorType = ref("")

// 부가서비스 전화번호 뒷자리 변수 선언
const inputRpsTelSecond = computed({
  get: () => rpsNoOption.value == '9999' ? rpsTelno.value.split('-')[2] || '' : rpsTelno.value.split('-')[1] || '',
  set: (value) => {
    const getValue = rpsTelno.value.split('-')
    
    rpsNoOption.value == '9999' ? getValue[2] = value : getValue[1] = value
    
    rpsTelno.value = getValue.join('-')

    emit('update:modelValue', rpsTelno.value);
  }
});

const inputRpsTelSecondRef: Ref<HTMLInputElement | null> = ref(null);
const isRpsTelSecondError = ref(false)
const isRpsTelSecondErrorType = ref('')

// 부가서비스 전화번호 값 핸들링
watch(rpsNoOption, (newVal, oldVal) => {
  if (!newVal) return;

  if(oldVal) {
    inputRpsNo.value = ''
    inputRpsTelSecond.value = ''
  }

  if(rpsNoOption.value == '9999') {
    rpsTelno.value = `${rpsNoOption.value}-${inputRpsNo.value || ''}-${inputRpsTelSecond.value || ''}`
  }
  else {
    rpsTelno.value = `${rpsNoOption.value}-${inputRpsTelSecond.value || ''}`
  }
  emit('update:modelValue', rpsTelno.value);
})

// 조회한 부가서비스 전화번호 세팅
watch(
  () => props.modelValue,
  (value) => {
    if (!value) {
      return;
    }
    if (props.type === 'rpsTel' && String(value).slice(0,4) != '9999') {
      const [a, m] = String(value).split('-');
      rpsNoOption.value = a || '';
      inputRpsTelSecond.value = m || '';
    } else if(props.type === 'rpsTel' && String(value).slice(0,4) == '9999'){
      const [a, m, l] = String(value).split('-');
      rpsNoOption.value = a || '';
      inputRpsNo.value = m || '';
      inputRpsTelSecond.value = l || '';
    }
  }
)

// 부가서비스 전화번호 직접입력 Delete 함수
const onInputRpsNoDelete = () => {
  const getValue = rpsTelno.value.split('-')

  getValue[1] = ""

  rpsTelno.value = getValue.join('-')
}

// 부가서비스 전화번호 직접입력 Focus 함수
const onInputRpsNoFocus = (): void => {
  if(inputRpsNoRef.value) {
    inputRpsNoRef.value?.focus();
  }
}

// 부가서비스 전화번호 뒷자리 Delete 함수
const onInputRpsTelSecondDelete = () => {
  const getValue = rpsTelno.value.split('-')

  rpsNoOption.value == '9999' ? getValue[2] = "" : getValue[1] = ""

  rpsTelno.value = getValue.join('-')
}

// 부가서비스 전화번호 뒷자리 Focus 함수
const onInputRpsTelSecondFocus = (): void => {
  if(inputRpsTelSecondRef.value) {
    inputRpsTelSecondRef.value?.focus();
  }
}

// 부가서비스 전화번호 필수 값 체크 함수
const onRpsTelnoValidate = (isCheck: boolean) => {
  
  const checkRpsTelnoFlag = ref(true);

  if(isCheck == false) {
    isRpsNoOptionError.value = false
    isRpsNoOptionErrorType.value = ""
    isInputRpsNoError.value = false
    isInputRpsNoErrorType.value = ""
    isRpsTelSecondError.value = false
    isRpsTelSecondErrorType.value = ""
    isRpsTelnoError.value = false
    isRpsTelnoErrorType.value = ""
    return checkRpsTelnoFlag.value = false
  }
  else if(isCheck == true) {

    isRpsNoOptionError.value = false
    isRpsNoOptionErrorType.value = ""
    isInputRpsNoError.value = false
    isInputRpsNoErrorType.value = ""
    isRpsTelSecondError.value = false
    isRpsTelSecondErrorType.value = ""
    isRpsTelnoError.value = false
    isRpsTelnoErrorType.value = ""

    // 부가서비스 전화번호 Null 체크
    if(rpsNoOption.value == null || rpsNoOption.value == "") {
      isRpsNoOptionError.value = true
      isRpsNoOptionErrorType.value = 'null'
      return checkRpsTelnoFlag.value = false;
    }

    isRpsNoOptionError.value = false
    isRpsNoOptionErrorType.value = ""

    // 부가서비스 전화번호 직접입력 Null 체크
    if(rpsNoOption.value == '9999' && (inputRpsNo.value == null || inputRpsNo.value == "")) {
      onInputRpsNoFocus();
      isInputRpsNoError.value = true
      isInputRpsNoErrorType.value = 'null'
      return checkRpsTelnoFlag.value = false;
    }

    isInputRpsNoError.value = false
    isInputRpsNoErrorType.value = ""

    // 전화번호 뒷자리 Null 체크
    if(inputRpsTelSecond.value == null || inputRpsTelSecond.value == "") {
      onInputRpsTelSecondFocus();
      isRpsTelSecondError.value = true
      isRpsTelSecondErrorType.value = 'null'
      return checkRpsTelnoFlag.value = false;
    }
    
    isRpsTelSecondError.value = false
    isRpsTelSecondErrorType.value = ""

    // 전화번호 타입 체크
    if((rpsNoOption.value == '9999' && isNaN(Number(inputRpsNo.value))) || isNaN(Number(inputRpsTelSecond.value))) {
      if(rpsNoOption.value == '9999' && isNaN(Number(inputRpsNo.value))) {
        onInputRpsTelSecondFocus();
        isInputRpsNoError.value = true
        isInputRpsNoErrorType.value = 'type'
      } 
      else {
        onInputRpsTelSecondFocus();
        isRpsTelSecondError.value = true
        isRpsTelSecondErrorType.value = 'type'
      }

      isRpsTelnoError.value = true
      isRpsTelnoErrorType.value = 'type'
      return checkRpsTelnoFlag.value = false;
    }

    isInputRpsNoError.value = false
    isInputRpsNoErrorType.value = ""
    isRpsTelSecondError.value = false
    isRpsTelSecondErrorType.value = ""
    isRpsTelnoError.value = false
    isRpsTelnoErrorType.value = ""

    // 전화번호 정규식 체크
    if(!HconCommonRegExp.RPS_TELNO_REGEX.test(rpsNoOption.value != '9999' ? rpsTelno.value : rpsTelno.value.substring(rpsTelno.value.indexOf('-') +1))) {
      if(inputRpsTelSecond.value.length != 4) {
        onInputRpsTelSecondFocus();
        isRpsTelSecondError.value = true
        isRpsTelSecondErrorType.value = 'format'
      } 
      else {
        onInputRpsTelSecondFocus();
        isInputRpsNoError.value = true
        isInputRpsNoErrorType.value = 'format'
      }

      isRpsTelnoError.value = true
      isRpsTelnoErrorType.value = 'format'
      return checkRpsTelnoFlag.value = false;
    }

    isInputRpsNoError.value = false
    isInputRpsNoErrorType.value = ""
    isRpsTelSecondError.value = false
    isRpsTelSecondErrorType.value = ""
    isRpsTelnoError.value = false
    isRpsTelnoErrorType.value = ""

  }
  
  return checkRpsTelnoFlag.value = true
}

/**
 * type = password
 */
const inputPassword = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value);
  }
});

const inputPasswordRef: Ref<HTMLInputElement | null> = ref(null);

const showPassword = ref(false);
const showEye1 = computed(() => !!inputPassword.value);

// 비밀번호 Delete 함수
const onPasswordDelete = async () => {
  inputPassword.value = "";
  showPassword.value = false;
  onPasswordFocus();
  await nextTick();
}

const togglePassword2 = () => {
  showPassword.value = !showPassword.value;
}

// 비밀번호 Focus 함수
const onPasswordFocus = (): void => {
  if(inputPasswordRef.value) {
    setTimeout(() => {
      inputPasswordRef.value?.focus();
    }, 50);
    
  }
}

/**
 * type = business
 */
const inputNumber = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value);
  }
});

const inputNumberRef: Ref<HTMLInputElement | null> = ref(null);

// number Input 함수
const onInputNumber = (event: Event): void => {
  const target = event.target as HTMLInputElement;
  const cleanedValue = target.value.replace(/[^0-9]/g, '');
  
  target.value = cleanedValue;
  // inputNumber.value = cleanedValue;
  emit('update:modelValue', cleanedValue);
}

// number Delete 함수
const onNumberDelete = (): void => {
  inputNumber.value = "";
  onNumberFocus();
}

// number Focus 함수
const onNumberFocus = (): void => {
  if(inputNumberRef.value) {
    setTimeout(() => {
      inputNumberRef.value?.focus();
    }, 50);
    
  }
}

/**
 * type = file
 */

// 파일 찾기
const uploadName = ref<string>('')

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  let filename = "";

  if (target.files && target.files.length > 0) {
    filename = target.files[0].name;
  } else if (target.value) {
    filename = target.value.split('/').pop()?.split('\\').pop() || '';
  }

  uploadName.value = filename
}

// plus 버튼 클릭 이벤트
const handlePlusBtn = () => {
  emit('plusBtnClick')
}

/**
 * type = message
 */

 const inputMessageRef: Ref<HTMLInputElement | null> = ref(null);

// mesasge type Focus 함수
const onMessageFocus = (): void => {
  if(inputMessageRef.value) {
    setTimeout(() => {
      inputMessageRef.value?.focus();
    }, 50);
  }
}

const onInputMessage = (event: Event) => {
  const target = event.target as HTMLInputElement
  emit('update:modelValue', target.value)
}

const onInputMsgBlur = (event: Event): void => {
  const target = event.target as HTMLInputElement
  emit('blur', target.value)
}

/**
 * type = account
 * 계좌번호 전용 emit: update:accountValue (파일 상단에 정의)
 * emit 시, 은행코드 및 입력한 전체 accountValue Object를 emit 한다.
 */
 interface Account {
  bankCode: string,
  bank: string,
  accountNumber: string,
  depositor: string,
  identifyNo: string,
}

const emptyAccount: Account = {
  bankCode: '',
  bank: '',
  accountNumber: '',
  depositor: '',
  identifyNo: '',
}

const {codeDataList:hcFnnsCd, isLoading: isLoadingAccount} = useHconRefCommonCode('hc_fnns_cd', {enabled: props.type === 'account'}); //HCON은행

const isNoBankCdError = ref(false)
const isNoBankCdErrorType = ref("")
const isNoAccountError = ref(false)
const isNoAccountErrorType = ref("")
const isAccountNumError = ref(false)
const isAccountNumErrorType = ref("")

const isReadonly = computed(() => props.readonly)

// 계좌번호 필수 값 체크 함수
const onAccountValidate = (isCheck: boolean) => {
  
  const checkAccountFlag = ref(false);

  if(isCheck == false) {
    isNoBankCdError.value = false
    isNoBankCdErrorType.value = ""
    isNoAccountError.value = false
    isNoAccountErrorType.value = ""
    isAccountNumError.value = false
    isAccountNumErrorType.value = ""
    return checkAccountFlag.value = false
  }
  else if (isCheck == true) {

    isNoBankCdError.value = false
    isNoBankCdErrorType.value = ""
    isNoAccountError.value = false
    isNoAccountErrorType.value = ""
    isAccountNumError.value = false
    isAccountNumErrorType.value = ""

    // 은행코드 Null 체크
    if(accountModel.value.bankCode == null || accountModel.value.bankCode == "") {
      isNoBankCdError.value = true
      isNoBankCdErrorType.value = 'null'
      return checkAccountFlag.value = false; 
    }

    isNoBankCdError.value = false
    isNoBankCdErrorType.value = ""

    //계좌번호 체크
    if(accountModel.value.accountNumber == null || accountModel.value.accountNumber == "" 
    || accountModel.value.depositor == null || accountModel.value.depositor == ""
    || accountModel.value.identifyNo == null || accountModel.value.identifyNo == "") {
      isNoAccountError.value = true
      isNoAccountErrorType.value = 'null'
      return checkAccountFlag.value = false;
    }

    isNoAccountError.value = false
    isNoAccountErrorType.value = ""
    
    //예금주명 null 체크
    if(accountModel.value.accountNumber == null || accountModel.value.accountNumber == "") {
      isAccountNumError.value = true
      isAccountNumErrorType.value = 'null'
      return checkAccountFlag.value = false;
    }

    isAccountNumError.value = false
    isAccountNumErrorType.value = ""

    return checkAccountFlag.value = true
  }

  return checkAccountFlag.value
}

// 계좌정보 get/set
const accountModel = computed<Account>({
  get() {
    if (props.type !== 'account') {
      return emptyAccount;
    }
    const value = props.accountValue;

    if (
      typeof value === 'object' &&
      value !== null &&
      'bankCode' in value &&
      'accountNumber' in value &&
      'depositor' in value &&
      'identifyNo' in value
    ) {
      return value as Account;
    }
    return emptyAccount;
  },
  set(value) {
    emit('update:accountValue', value);
  }
});

// 계좌번호 입력 시 숫자만 입력되도록 제어
const onAccountNumInput = (event: Event) => {

  const target = event.target as HTMLInputElement;
  const cleanedValue = target.value.replace(/[^0-9]/g, '');

  accountModel.value = {
    ...accountModel.value,
    accountNumber: cleanedValue,
  }
}

// 계좌번호 x 아이콘 클릭 시
const onAccountNumInpDelete = (): void => {

  accountModel.value = {
    ...accountModel.value,
    accountNumber: "",
  }
}

// 계좌번호 입력 시 숫자만 입력되도록 제어
const onAccountIdentifyNoInput = (event: Event) => {

const target = event.target as HTMLInputElement;
const cleanedValue = target.value.replace(/[^0-9]/g, '');

accountModel.value = {
  ...accountModel.value,
  identifyNo: cleanedValue,
}
}

// 계좌번호 x 아이콘 클릭 시
const onAccountIdentifyNoInpDelete = (): void => {

accountModel.value = {
  ...accountModel.value,
  identifyNo: "",
}
}

// 예금주명 x 아이콘 클릭 시
const onAccountNameInpDelete = (): void => {
  accountModel.value = {
    ...accountModel.value,
    depositor: "",
  }
}

const onBankCodeChange = (event: Event) => {
  accountModel.value = {
    ...accountModel.value,
    bankCode: accountModel.value.bankCode,
    bank: hcFnnsCd.value.find((item : {commDtlCd :string; commDtlCdNm : string}) => item.commDtlCd === accountModel.value.bankCode).commDtlCdNm,
  }
}

defineOptions({
  inheritAttrs: false
})

// 부모컴포넌트에서 해당 함수 사용
defineExpose({
  onInputTextFocus,
  onNumberFocus,
  onPasswordFocus,
  onMessageFocus,
  onEmailAdrValidate,
  onCbleTelnoValidate,
  onRpsTelnoValidate,
  onAccountValidate,
})


/*
 * type = pay
 */
// 원 표시 인풋창
const inputPayNumDisplay = computed(() => {
  const num = Number(String(props.modelValue).replace(/[^0-9]/g, '')) || 0;
  return num.toLocaleString();
})

const onInputPayNumber = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const onlyNumber = target.value.replace(/[^0-9]/g, '');
  const numValue = onlyNumber === '' ? '0' : onlyNumber;

  emit('update:modelValue', numValue);

  target.value = Number(onlyNumber).toLocaleString();
}

const onInputPayBlur = (event: Event): void => {
  const target = event.target as HTMLInputElement;

  emit('blur', target.value);
}

// plus 버튼 클릭 이벤트
const handleAccountBtn = () => {
  emit('accountBtnClick')
}

// search
const handleSearch = () => {
  emit('searchBtnClick')  
}

// number 하이드레이션 오류로 렌더링 중일때 readonly 처리
const isMounted = ref(false);
onMounted(() => isMounted.value = true);

/*
 * type = count
 */
const currentNumber = computed(() => {
  if (typeof props.modelValue === 'boolean') return props.modelValue ? 1 : 0;
  const num = Number(props.modelValue);
  return isNaN(num) ? 0 : num;
})

// 환금성에 따른 최소 수량 기준
const actualMin = computed(() => {
  return props.isCashable ? props.cashableMin : props.min;
})

onMounted(() => {
  const actualMinVal = actualMin.value ?? 0;

  if (currentNumber.value < actualMinVal) {
    updateValue(currentNumber.value)
  }
})

// isCashable 상태가 변할 때만 감시하여 보정
watch(actualMin, (newMin) => {
  let value = newMin ?? 0;
  if (currentNumber.value < value) {
    updateValue(currentNumber.value)
  }
})

const updateValue = (newVal: number) => {
  let value = newVal;

  const maxValue = props.max ?? 0;
  const actualMinVal = actualMin.value ?? 0;

  if (value < actualMinVal) value = actualMinVal;
  if (value > maxValue) value = maxValue;

  emit('update:modelValue', String(value));
}

const onPlus = () => {
  const maxValue = props.max ?? 0;

  if (currentNumber.value < maxValue) {
    updateValue(currentNumber.value + 1);
  }
}

const onMinus = () => {
  const actualMinVal = actualMin.value ?? 0;
  if (currentNumber.value > actualMinVal) {
    updateValue(currentNumber.value - 1);
  }
}

const onInputCount = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const val = parseInt(target.value.replace(/[^0-9]/g, ''));
  target.value = isNaN(val) ? String(0) : String(val)

  let num = Number(val);
  const maxValue = props.max ?? 0;

  if (props.max !== null && num > maxValue) {
    num = maxValue;
    target.value = String(num)
  }

  updateValue(isNaN(val) ? 0: val);
}

const onBlurCount = (event: Event) => {
  const target = event.target as HTMLInputElement;
  let numVal = parseInt(target.value.replace(/[^0-9]/g, ''));
  let minLimit = actualMin.value ?? 1

  if(isNaN(numVal) || numVal < minLimit){
    const fixedVal = String(minLimit)
    target.value = fixedVal
    emit('update:modelValue', fixedVal)
  }
}

const onPasteCount = (event: ClipboardEvent) => {
  
  event.preventDefault()
  const pastedDate = event.clipboardData?.getData('text') || ''

  let numVal = parseInt(pastedDate, 10)
  let finalVal = pastedDate
  let minLimit = actualMin.value ?? 1

  if(!isNaN(numVal)){
    if(numVal < minLimit) {
      finalVal = String(minLimit)
    } else {
      finalVal = String(numVal)
    }
    
    const target = event.target as HTMLInputElement
    target.value = finalVal
  }

  emit('update:modelValue', finalVal)
}

</script>

<style lang="scss" scoped>
</style>